import { useEffect, useState } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, TextField,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import Save from "@mui/icons-material/Save";
import DialogDateField from "./DialogDateField";
import LoadingIndicator from './LoadingIndicator';
import { normalizeDialogDate } from "./dialogDate";


export default function UmsatzDialog({
  open, onClose, onSave, umsatz, art, kategorien, betriebsjahre, currentBetriebsjahr, yearLabel = "Betriebsjahr", loading = false,
}) {
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      datum: umsatz?.datum || "",
      nr: umsatz?.nr ?? "",
      zweck: umsatz?.zweck || "",
      betrag: umsatz?.betrag ?? "",
      kategorie_id: umsatz?.kategorie_id ?? "",
      betriebsjahr_id: umsatz?.betriebsjahr_id ?? currentBetriebsjahr ?? "",
      art,
    });
    setError("");
  }, [open, umsatz, art, currentBetriebsjahr]);

  const validCategory = kategorien.some(item => String(item.id) === String(form.kategorie_id));
  useEffect(() => {
    setForm(current => current.kategorie_id && !kategorien.some(item => String(item.id) === String(current.kategorie_id))
      ? { ...current, kategorie_id: "" } : current);
  }, [kategorien]);

  const setField = (name) => (event) => {
    setForm((current) => ({ ...current, [name]: event.target.value }));
  };

  const handleSave = async () => {
    if (!validCategory) return;
    setSaving(true);
    setError("");
    try {
      await onSave({ ...form, datum: normalizeDialogDate(form.datum), art });
    } catch (saveError) {
      setError(saveError.response?.data?.error || "Der Umsatz konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{umsatz ? `${art === "Ein" ? "Einnahme" : "Ausgabe"} bearbeiten` : `${art === "Ein" ? "Einnahme" : "Ausgabe"} hinzufügen`}</DialogTitle>
      <DialogContent role="group" aria-label="Buchungsdaten" sx={{ display: "grid", gridTemplateColumns: art === "Aus" ? "1fr" : { xs: "1fr", sm: "1fr 1fr" }, gap: 2, pt: "12px !important" }}>
        {loading && <LoadingIndicator label="Kategorien werden geladen …" sx={{ gridColumn: '1 / -1' }} />}
        {error && <Alert severity="error" sx={{ gridColumn: "1 / -1" }}>{error}</Alert>}
        <FormControl size="small" fullWidth>
          <InputLabel id="umsatz-jagdjahr-label">{yearLabel}</InputLabel>
          <Select labelId="umsatz-jagdjahr-label" label={yearLabel} inputProps={{ "aria-label": yearLabel }} value={form.betriebsjahr_id ?? ""} onChange={setField("betriebsjahr_id")}>
            {betriebsjahre.map((item) => <MenuItem key={item.Id} value={item.Id}>{item.Name}</MenuItem>)}
          </Select>
        </FormControl>
        <DialogDateField size="small" label="Datum" value={form.datum || ""} onChange={setField("datum")} slotProps={{ inputLabel: { shrink: true }, htmlInput: { "aria-label": "Datum" } }} />
        {art === "Ein" && <TextField size="small" type="number" label="Nr" value={form.nr ?? ""} onChange={setField("nr")} slotProps={{ htmlInput: { "aria-label": "Nr" } }} />}
        <TextField size="small" type="number" label="Betrag" value={form.betrag ?? ""} onChange={setField("betrag")} slotProps={{ htmlInput: { min: 0, step: "0.01", "aria-label": "Betrag" } }} required />
        <TextField size="small" label="Zweck" value={form.zweck || ""} onChange={setField("zweck")} slotProps={{ htmlInput: { "aria-label": "Zweck" } }} required sx={{ gridColumn: "1 / -1" }} />
        <FormControl size="small" fullWidth required sx={{ gridColumn: "1 / -1" }}>
          <InputLabel id="umsatz-kategorie-label">Kategorie</InputLabel>
          <Select labelId="umsatz-kategorie-label" label="Kategorie" inputProps={{ "aria-label": "Kategorie" }} value={validCategory ? form.kategorie_id : ""} onChange={setField("kategorie_id")}>
            {kategorien.map((item) => <MenuItem key={item.id} value={item.id}>{item.kuerzel} – {item.beschreibung}</MenuItem>)}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" startIcon={<Close />} onClick={onClose} disabled={saving}>Abbrechen</Button>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || !form.betriebsjahr_id || !form.zweck?.trim() || form.betrag === "" || !validCategory}>Speichern</Button>
      </DialogActions>
    </Dialog>
  );
}
